import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { 
  Newspaper, Plus, Search, Edit, Trash2, Eye, Send, 
  Pin, Clock, Loader2, CheckCircle, AlertTriangle, Bell,
  Calendar, TrendingUp, Archive, Megaphone
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const CATEGORIES = [
  { value: 'Announcement', label: 'Announcement' },
  { value: 'Update', label: 'Update' },
  { value: 'Event', label: 'Event' },
  { value: 'Policy Change', label: 'Policy Change' },
  { value: 'Achievement', label: 'Achievement' },
  { value: 'Reminder', label: 'Reminder' },
  { value: 'Other', label: 'Other' },
];

const PRIORITIES = [
  { value: 'normal', label: 'Normal', color: 'bg-gray-100 text-gray-800' },
  { value: 'important', label: 'Important', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-800' },
];

const STATUS_COLORS = {
  draft: 'bg-yellow-100 text-yellow-800',
  published: 'bg-green-100 text-green-800',
  archived: 'bg-gray-100 text-gray-800',
};

const NewsManagement = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    summary: '',
    category: 'Announcement',
    priority: 'normal',
    is_pinned: false,
    show_on_dashboard: true,
    expires_at: '',
  });

  const { getAuthHeaders, currentOffice } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPosts();
  }, [currentOffice]);

  const fetchPosts = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }

      const response = await fetch(`${API_URL}/api/news/admin`, { headers });
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching news posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setProcessing(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }

      const payload = {
        ...formData,
        expires_at: formData.expires_at || null,
      };

      const response = await fetch(`${API_URL}/api/news/`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setShowCreateModal(false);
        resetForm();
        fetchPosts();
      }
    } catch (error) {
      console.error('Error creating news post:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdate = async () => {
    if (!selectedPost) return;
    setProcessing(true);
    try {
      const headers = getAuthHeaders();

      const payload = {
        ...formData,
        expires_at: formData.expires_at || null,
      };

      const response = await fetch(`${API_URL}/api/news/admin/${selectedPost.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setShowEditModal(false);
        resetForm();
        fetchPosts();
      }
    } catch (error) {
      console.error('Error updating news post:', error);
    } finally {
      setProcessing(false);
    }
  };

  const handlePublish = async (post) => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/news/admin/${post.id}/publish`, {
        method: 'POST',
        headers,
      });

      if (response.ok) {
        fetchPosts();
      }
    } catch (error) {
      console.error('Error publishing news post:', error);
    }
  };

  const handleDelete = async () => {
    if (!selectedPost) return;
    setProcessing(true);
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/news/admin/${selectedPost.id}`, {
        method: 'DELETE',
        headers,
      });

      if (response.ok) {
        setShowDeleteModal(false);
        setSelectedPost(null);
        fetchPosts();
      }
    } catch (error) {
      console.error('Error deleting news post:', error);
    } finally {
      setProcessing(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      summary: '',
      category: 'Announcement',
      priority: 'normal',
      is_pinned: false,
      show_on_dashboard: true,
      expires_at: '',
    });
    setSelectedPost(null);
  };

  const openEditModal = (post) => {
    setSelectedPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      summary: post.summary || '',
      category: post.category,
      priority: post.priority,
      is_pinned: post.is_pinned,
      show_on_dashboard: post.show_on_dashboard,
      expires_at: post.expires_at ? post.expires_at.split('T')[0] : '',
    });
    setShowEditModal(true);
  };

  const openViewModal = (post) => {
    setSelectedPost(post);
    setShowViewModal(true);
  };

  const openDeleteModal = (post) => {
    setSelectedPost(post);
    setShowDeleteModal(true);
  };

  // Filter posts
  const filteredPosts = posts.filter(post => {
    if (activeTab !== 'all' && post.status !== activeTab) return false;
    if (searchQuery && !post.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (selectedCategory !== 'all' && post.category !== selectedCategory) return false;
    return true;
  });

  // Stats
  const stats = {
    total: posts.length,
    published: posts.filter(p => p.status === 'published').length,
    draft: posts.filter(p => p.status === 'draft').length,
    pinned: posts.filter(p => p.is_pinned && p.status === 'published').length,
  };

  const getPriorityBadge = (priority) => {
    const p = PRIORITIES.find(pr => pr.value === priority);
    return p ? p.color : 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Newsroom</h1>
          <p className="text-gray-600">Create and manage news updates for your office</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} data-testid="create-news-button">
          <Plus className="h-4 w-4 mr-2" />
          Create Post
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Newspaper className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Posts</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Published</p>
                <p className="text-2xl font-bold">{stats.published}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Drafts</p>
                <p className="text-2xl font-bold">{stats.draft}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Pin className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Pinned</p>
                <p className="text-2xl font-bold">{stats.pinned}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search posts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-news-input"
                />
              </div>
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabs and Posts List */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All ({stats.total})</TabsTrigger>
          <TabsTrigger value="published">Published ({stats.published})</TabsTrigger>
          <TabsTrigger value="draft">Drafts ({stats.draft})</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-4">
          {filteredPosts.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Newspaper className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900">No posts found</h3>
                <p className="text-gray-500 mt-1">Create your first news post to get started</p>
                <Button className="mt-4" onClick={() => setShowCreateModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Post
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredPosts.map(post => (
                <Card key={post.id} className="hover:shadow-md transition-shadow" data-testid={`news-card-${post.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {post.is_pinned && (
                            <Pin className="h-4 w-4 text-purple-600" />
                          )}
                          <h3 className="font-semibold text-lg">{post.title}</h3>
                          <Badge className={STATUS_COLORS[post.status]}>
                            {post.status}
                          </Badge>
                          <Badge className={getPriorityBadge(post.priority)}>
                            {post.priority}
                          </Badge>
                          {post.is_new && (
                            <Badge className="bg-blue-100 text-blue-800">New</Badge>
                          )}
                        </div>
                        <p className="text-gray-600 text-sm mb-2 line-clamp-2">
                          {post.summary || post.content.substring(0, 150)}...
                        </p>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Megaphone className="h-4 w-4" />
                            {post.category}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {new Date(post.created_at).toLocaleDateString()}
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="h-4 w-4" />
                            {post.view_count} views
                          </span>
                          {post.created_by_name && (
                            <span>By {post.created_by_name}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <Button variant="outline" size="sm" onClick={() => openViewModal(post)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openEditModal(post)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {post.status === 'draft' && (
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handlePublish(post)}
                            data-testid={`publish-news-${post.id}`}
                          >
                            <Send className="h-4 w-4 mr-1" />
                            Publish
                          </Button>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-red-600 hover:text-red-700"
                          onClick={() => openDeleteModal(post)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal || showEditModal} onOpenChange={(open) => {
        if (!open) {
          setShowCreateModal(false);
          setShowEditModal(false);
          resetForm();
        }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="news-form-modal">
          <DialogHeader>
            <DialogTitle>
              {showEditModal ? 'Edit Post' : 'Create News Post'}
            </DialogTitle>
            <DialogDescription>
              {showEditModal ? 'Update the news post details' : 'Create a new announcement for your office'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Office Closure Notice"
                  data-testid="news-title-input"
                />
              </div>

              <div>
                <Label htmlFor="category">Category</Label>
                <Select value={formData.category} onValueChange={(val) => setFormData({ ...formData, category: val })}>
                  <SelectTrigger data-testid="news-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="priority">Priority</Label>
                <Select value={formData.priority} onValueChange={(val) => setFormData({ ...formData, priority: val })}>
                  <SelectTrigger data-testid="news-priority-select">
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2">
                <Label htmlFor="summary">Summary (optional)</Label>
                <Input
                  id="summary"
                  value={formData.summary}
                  onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                  placeholder="Brief description for preview"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="content">Content *</Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Enter the full content..."
                  rows={8}
                  data-testid="news-content-input"
                />
              </div>

              <div>
                <Label htmlFor="expires_at">Expires On (optional)</Label>
                <Input
                  id="expires_at"
                  type="date"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                />
              </div>

              <div className="flex items-center justify-end gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="is_pinned"
                    checked={formData.is_pinned}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_pinned: checked })}
                  />
                  <Label htmlFor="is_pinned" className="font-normal">Pin to top</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="show_on_dashboard"
                    checked={formData.show_on_dashboard}
                    onCheckedChange={(checked) => setFormData({ ...formData, show_on_dashboard: checked })}
                  />
                  <Label htmlFor="show_on_dashboard" className="font-normal">Show on dashboard</Label>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowCreateModal(false);
              setShowEditModal(false);
              resetForm();
            }}>
              Cancel
            </Button>
            <Button 
              onClick={showEditModal ? handleUpdate : handleCreate}
              disabled={!formData.title || !formData.content || processing}
              data-testid="save-news-button"
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {showEditModal ? 'Update Post' : 'Create Post'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              {selectedPost?.is_pinned && <Pin className="h-5 w-5 text-purple-600" />}
              {selectedPost?.title}
              <Badge className={STATUS_COLORS[selectedPost?.status]}>
                {selectedPost?.status}
              </Badge>
              <Badge className={getPriorityBadge(selectedPost?.priority)}>
                {selectedPost?.priority}
              </Badge>
            </DialogTitle>
          </DialogHeader>

          {selectedPost && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4 text-sm text-gray-500 pb-4 border-b">
                <span className="flex items-center gap-1">
                  <Megaphone className="h-4 w-4" />
                  {selectedPost.category}
                </span>
                <span className="flex items-center gap-1">
                  <Eye className="h-4 w-4" />
                  {selectedPost.view_count} views
                </span>
                {selectedPost.published_at && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    Published: {new Date(selectedPost.published_at).toLocaleDateString()}
                  </span>
                )}
              </div>

              <div className="prose max-w-none">
                <div className="whitespace-pre-wrap text-gray-700">
                  {selectedPost.content}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewModal(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setShowViewModal(false);
              openEditModal(selectedPost);
            }}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent data-testid="delete-news-modal">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Post</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this news post? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {selectedPost && (
            <div className="p-4 bg-red-50 rounded-lg">
              <p className="font-medium">{selectedPost.title}</p>
              <p className="text-sm text-gray-600">{selectedPost.category}</p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDelete}
              disabled={processing}
              data-testid="confirm-delete-news"
            >
              {processing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete Post
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewsManagement;
